import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { addedAssetsMessages as messages } from './addedAssetsMessages';
import { closeAddAssetDialog } from './addAssetDialogStore';
import { YouTubeLinkState, youtubeThumbnailUrl } from './youtubeLinkState';
import styles from './YouTubeLinkField.module.scss';

interface YouTubeLinkFieldProps {
  value: string;
  state: YouTubeLinkState;
  /** The sentence of a refusal of the server, under the field. */
  error: string | null;
  onChange: (value: string) => void;
}

/**
 * "YouTube link" (design a3): the field, the error under it ("This is not a YouTube video link.",
 * the server refusals), the picture of the parsed video, and the info note for a video that is not
 * on a connected channel, with "Connect a channel".
 */
const YouTubeLinkFieldComponent: React.FC<YouTubeLinkFieldProps> = ({ value, state, error, onChange }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const shownError = state.notYouTube ? intl.formatMessage(messages.errorNotYouTube) : error;
  const connect = () => {
    closeAddAssetDialog();
    router.push(SMLRoutes.promoteChannels);
  };

  return (
    <>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="added-youtube-link">
          {intl.formatMessage(messages.linkLabel)}
        </label>
        <input
          id="added-youtube-link"
          className={styles.input}
          type="url"
          inputMode="url"
          autoComplete="off"
          placeholder={intl.formatMessage(messages.linkPlaceholder)}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={shownError !== null}
          aria-describedby={shownError === null ? undefined : 'added-youtube-error'}
        />
        {shownError !== null && (
          <span id="added-youtube-error" className={styles.error} role="alert">
            {shownError}
          </span>
        )}
      </div>
      {state.videoId !== null && (
        <div className={styles.ytRow} data-testid="youtube-link-preview">
          <span className={styles.ytThumb}>
            <img src={youtubeThumbnailUrl(state.videoId)} alt="" />
          </span>
          <span className={styles.ytText}>
            <b>{intl.formatMessage(messages.linkPreview, { id: state.videoId })}</b>
          </span>
        </div>
      )}
      {state.otherChannel && (
        <div className={styles.info} data-testid="youtube-other-channel">
          <span>{intl.formatMessage(messages.otherChannel)}</span>
          <button type="button" className={styles.link} onClick={connect}>
            {intl.formatMessage(messages.connectChannel)}
          </button>
        </div>
      )}
    </>
  );
};

export const YouTubeLinkField = TypedMemo(YouTubeLinkFieldComponent);
